import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { History, RefreshCw, X, ChevronRight, CalendarCheck } from 'lucide-react';
import { getApiUrl } from '../../config/api';
import { formatDateTime } from '../../utils/dateUtils';
import { getUserWarehouse } from '../../constants/userLocation';
import './styles/RescheduledAppointments.css';

const LOCATIONS = ['Pomona', 'Lakewood', 'Israel'];

// EnteredBy is stored as "OriginalCreator | Updated by: CurrentUser" once edited;
// the most recently attributed person is who we filter by.
const getAttributedUser = (enteredBy) => {
  if (!enteredBy) return '';
  const idx = enteredBy.indexOf(' | Updated by:');
  if (idx !== -1) {
    return enteredBy.slice(idx + ' | Updated by:'.length).trim();
  }
  return enteredBy.trim();
};

const isSameCalendarDay = (a, b) => {
  if (!a || !b) return false;
  const dateA = new Date(a);
  const dateB = new Date(b);
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
};

const formatAge = (dtStr) => {
  if (!dtStr) return '';
  const diff = Date.now() - new Date(dtStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const RescheduledAppointments = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [customerName, setCustomerName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [includePast, setIncludePast] = useState(false);
  const [location, setLocation] = useState('');
  const debounceRef = useRef(null);
  const isFirstRun = useRef(true);

  const fetchRescheduled = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.customerName) params.set('customerName', filters.customerName);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      if (filters.includePast) params.set('includePast', 'true');

      const query = params.toString();
      const response = await fetch(getApiUrl(`/api/appointments/rescheduled${query ? `?${query}` : ''}`));
      if (!response.ok) {
        throw new Error('Failed to fetch rescheduled appointments');
      }

      const data = await response.json();
      const filtered = (data.appointments || [])
        .filter((apt) => !isSameCalendarDay(apt.OGEventStart, apt.NewEventStart))
        .sort((a, b) => new Date(a.OGEventStart) - new Date(b.OGEventStart));
      setAppointments(filtered);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch immediately on mount, then debounce refetches as filters change
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      fetchRescheduled();
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchRescheduled({ customerName, startDate, endDate, includePast });
    }, 350);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName, startDate, endDate, includePast]);

  const clearFilters = () => {
    setCustomerName('');
    setStartDate('');
    setEndDate('');
    setIncludePast(false);
    setLocation('');
  };

  const hasFilters = customerName || startDate || endDate || includePast || location;

  const displayedAppointments = location
    ? appointments.filter((apt) => getUserWarehouse(getAttributedUser(apt.EnteredBy)) === location)
    : appointments;

  const isPast = (apt) => {
    const eventStart = apt.NewEventStart;
    if (!eventStart) return false;
    return new Date(eventStart) < new Date();
  };

  const handleRowClick = (apt) => {
    const datePart = (apt.NewEventStart || '').split('T')[0];
    if (!datePart) return;
    navigate(`/appointment?appointmentId=${apt.ID}&date=${datePart}`);
  };

  return (
    <div className="rescheduled-container">
      <div className="rescheduled-header">
        <div className="rescheduled-heading">
          <h1 className="rescheduled-title">
            <History size={22} className="rescheduled-title-icon" aria-hidden="true" />
            Rescheduled Appointments
          </h1>
        </div>
        <button
          className="rescheduled-btn rescheduled-btn-primary"
          onClick={() => fetchRescheduled({ customerName, startDate, endDate, includePast })}
          disabled={loading}
        >
          <RefreshCw size={15} className={loading ? 'rescheduled-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="rescheduled-filters">
        <div className="filter-group">
          <label htmlFor="rescheduled-customer">Customer</label>
          <input
            id="rescheduled-customer"
            type="text"
            placeholder="Search by name..."
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            className="rescheduled-search-input"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="rescheduled-from">From</label>
          <input
            id="rescheduled-from"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rescheduled-date-input"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="rescheduled-to">To</label>
          <input
            id="rescheduled-to"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rescheduled-date-input"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="rescheduled-location">Location</label>
          <select
            id="rescheduled-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="rescheduled-select"
          >
            <option value="">All Locations</option>
            {LOCATIONS.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>

        <label className="ios-toggle">
          <input
            type="checkbox"
            checked={includePast}
            onChange={(e) => setIncludePast(e.target.checked)}
          />
          <span className="ios-toggle-track">
            <span className="ios-toggle-thumb" />
          </span>
          <span className="ios-toggle-label">Show past appointments</span>
        </label>

        {hasFilters && (
          <button className="rescheduled-btn rescheduled-btn-ghost" onClick={clearFilters}>
            <X size={14} />
            Clear Filters
          </button>
        )}
      </div>

      {loading && (
        <div className="rescheduled-message">
          <p>Loading rescheduled appointments...</p>
        </div>
      )}

      {error && (
        <div className="rescheduled-error">
          <p>{error}</p>
          <button
            onClick={() => fetchRescheduled({ customerName, startDate, endDate, includePast })}
            className="rescheduled-btn rescheduled-btn-ghost"
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        displayedAppointments.length === 0 ? (
          <div className="rescheduled-empty">
            <CalendarCheck size={28} className="rescheduled-empty-icon" aria-hidden="true" />
            <p>
              {hasFilters
                ? 'No rescheduled appointments match these filters.'
                : "Nothing's been rescheduled — you're all caught up."}
            </p>
          </div>
        ) : (
          <table className="rescheduled-table">
            <thead>
              <tr>
                <th>Original Date</th>
                <th>New Date</th>
                <th>Customer Name</th>
                <th>Status</th>
                <th>Entered By</th>
              </tr>
            </thead>
            <tbody>
              {displayedAppointments.map((apt) => {
                const past = isPast(apt);
                return (
                  <tr
                    key={apt.ID}
                    onClick={() => handleRowClick(apt)}
                    className={`rescheduled-row${past ? ' rescheduled-row-past' : ''}`}
                    title="Click to view on calendar"
                  >
                    <td className="rescheduled-og-date">{formatDateTime(apt.OGEventStart)}</td>
                    <td className="rescheduled-new-date">
                      <span>{formatDateTime(apt.NewEventStart)}</span>
                      {past ? (
                        <span className="rescheduled-past-badge">Past</span>
                      ) : (
                        <span className="rescheduled-age-badge">{formatAge(apt.dtOGDateSet)}</span>
                      )}
                    </td>
                    <td>{apt.ClientName || '-'}</td>
                    <td>
                      <span className={`status-badge ${(apt.AppointmentStatus || 'Booked').toLowerCase().replace(/\s+/g, '-')}`}>
                        {apt.AppointmentStatus || 'Booked'}
                      </span>
                    </td>
                    <td className="rescheduled-entered-cell">
                      <span>{apt.EnteredBy || '-'}</span>
                      <ChevronRight size={16} className="rescheduled-row-chevron" aria-hidden="true" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      )}
    </div>
  );
};

export default RescheduledAppointments;
